import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DonationBill } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/DonationBill/DonationBill";
import { JointBillEmptyProvidersMessage } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/JointBillEmptyProvidersMessage/JointBillEmptyProvidersMessage";
import { JointBillFooterTotalCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/JointBillFooterTotalCell/JointBillFooterTotalCell";
import { JointBillHeaderNameCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/JointBillHeaderNameCell/JointBillHeaderNameCell";
import { JointBillHeaderStatusCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/JointBillHeaderStatusCell/JointBillHeaderStatusCell";
import { JointBillHeaderTotalCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/JointBillHeaderTotalCell/JointBillHeaderTotalCell";
import { MobileTotals } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/MobileTotals/MobileTotals";
import { NonProfitProviders } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/NonProfitProviders/NonProfitProviders";
import { ProviderBill } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/ProviderBill";
import { ProviderCategories } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderCategories/ProviderCategories";
import { RequestedSpBill } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/RequestedServiceProviders/components/RequestedSpBill/RequestedSpBill";
import { JointBillTableLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableLayout/JointBillTableLayout";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { JointBillContext } from "@features/JointBillList/utils";
import { JointBillStatusBadge } from "@features/JointBillStatusBadge/JointBillStatusBadge";
import { type ProjectTypeId } from "@features/Login/types";
import { ButtonLink } from "@components/Button";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { FEATURE_FLAG } from "@services/featureConfig/types";
import {
  PeriodState,
  type IBillData,
  type IDonationData,
  type IJointBillServicePeriodData,
  type IJointBillServicePeriodVoucher,
  type IOverviewOtherUserData,
  type IRequestedSpBillData,
  type SelectionState,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { JointBillAdverts } from "./components/JointBillAdverts/JointBillAdverts";
import { JointBillVoucher } from "./components/JointBillVoucher/JointBillVoucher";
import { RequestedServiceProviders } from "./components/RequestedServiceProviders/RequestedServiceProviders";

interface IJointBillTableProps {
  jointBillServiceId: IJointBillServicePeriodData["jointBillServiceId"];
  orderId: number;
  periodId: number;
  houseDisplayName: string;
  otherUsers?: IOverviewOtherUserData[];
  planName: string;
  periodState: PeriodState;
  paidAmount?: number;
  totalSum?: number;
  totalAmount?: number;
  totalExpectedPayment?: number;
  unpaidAmount?: number;
  onChangePlanClick?: () => void;
  isEditHouseLoading?: boolean;
  onEditHouseClick?: () => void;
  notification?: JSX.Element | null;
  mobileTotals?: JSX.Element | null;
  bills: IBillData[];
  donations: IDonationData[];
  jointBillSelectionState?: SelectionState;
  isJointBillCheckboxDisabled?: boolean;
  isJointBillCheckboxLoading?: boolean;
  onJointBillCheckboxClick?: () => void;
  isBillDownloadEnabled?: boolean;
  onBillDownloadClick?: () => void;
  isBillDownloadLoading?: boolean;
  projectTypeId?: ProjectTypeId;
  isProjectBillDownloadEnabled?: boolean;
  onProjectBillDownloadClick?: () => void;
  isProjectBillDownloadLoading?: boolean;
  isOpenPeriodButtonEnabled?: boolean;
  onOpenPeriodButtonClick?: () => void;
  isBillDefaultOpen?: boolean;
  canBePaid?: boolean;
  providerBillPlanName: string;
  requestedSpBillData: IRequestedSpBillData[];
  isServiceProviderCategorySuggestionEnabled: boolean;
  vouchers: IJointBillServicePeriodVoucher[];
}

const MAX_SUMMARY_ENTRIES = 3;

export const JointBillTable: React.FC<IJointBillTableProps> = ({
  jointBillServiceId,
  orderId,
  periodId,
  houseDisplayName,
  otherUsers,
  planName,
  periodState,
  paidAmount,
  totalSum,
  totalAmount,
  totalExpectedPayment,
  unpaidAmount,
  onChangePlanClick,
  isEditHouseLoading,
  onEditHouseClick,
  notification,
  bills,
  donations,
  jointBillSelectionState,
  isJointBillCheckboxDisabled,
  isJointBillCheckboxLoading,
  onJointBillCheckboxClick,
  isBillDownloadEnabled,
  onBillDownloadClick,
  isBillDownloadLoading,
  projectTypeId,
  isProjectBillDownloadEnabled,
  onProjectBillDownloadClick,
  isProjectBillDownloadLoading,
  isOpenPeriodButtonEnabled,
  onOpenPeriodButtonClick,
  isBillDefaultOpen = true,
  canBePaid,
  providerBillPlanName,
  requestedSpBillData,
  isServiceProviderCategorySuggestionEnabled,
  vouchers,
}) => {
  const { t } = useTranslation();
  const {
    isNonProfitProvidersEnabled,
    isRequestedServiceProvidersCollapseEnabled,
  } = useFeatureConfig();
  const { poaSpRequests, isSummaryMode, isArchiveMode } =
    useContext(JointBillContext);
  const { nonProfitProviders } = useContext(JointBillContext);
  const shouldDisplayNonProfits =
    !isSummaryMode &&
    isNonProfitProvidersEnabled &&
    !isNullOrUndefined(nonProfitProviders) &&
    nonProfitProviders?.length > 0 &&
    canBePaid;

  const billsToShow = useMemo(
    () => (isSummaryMode ? bills.slice(0, MAX_SUMMARY_ENTRIES) : bills),
    [bills, isSummaryMode],
  );

  const donationsToShow = useMemo(() => {
    let entries: IDonationData[];
    if (isSummaryMode) {
      if (billsToShow.length < MAX_SUMMARY_ENTRIES) {
        entries = donations.slice(0, MAX_SUMMARY_ENTRIES - billsToShow.length);
      } else {
        entries = [];
      }
    } else {
      entries = donations;
    }

    return entries;
  }, [billsToShow.length, donations, isSummaryMode]);

  const shouldDisplayMore =
    bills.length + donations.length > MAX_SUMMARY_ENTRIES;

  return (
    <JointBillTableLayout
      id={jointBillServiceId}
      orderId={orderId}
      headerNameCell={
        <JointBillHeaderNameCell
          houseDisplayName={houseDisplayName}
          otherUsers={otherUsers}
          planName={planName}
          onChangePlanClick={onChangePlanClick}
          isEditHouseLoading={isEditHouseLoading}
          onEditHouseClick={onEditHouseClick}
          billStatusBadge={
            <JointBillStatusBadge periodState={periodState} badgeSize="small" />
          }
          isBillDownloadEnabled={isBillDownloadEnabled}
          onBillDownloadClick={onBillDownloadClick}
          isBillDownloadLoading={isBillDownloadLoading}
          projectTypeId={projectTypeId}
          isProjectBillDownloadEnabled={isProjectBillDownloadEnabled}
          onProjectBillDownloadClick={onProjectBillDownloadClick}
          isProjectBillDownloadLoading={isProjectBillDownloadLoading}
        />
      }
      mobileTotals={
        <MobileTotals paidAmount={paidAmount} unpaidAmount={unpaidAmount} />
      }
      headerStatusCell={
        <JointBillHeaderStatusCell
          periodState={periodState}
          isOpenPeriodButtonEnabled={isOpenPeriodButtonEnabled}
          onOpenPeriodButtonClick={onOpenPeriodButtonClick}
        />
      }
      headerTotalCell={
        <JointBillHeaderTotalCell
          paidAmount={paidAmount}
          unpaidAmount={unpaidAmount}
          onChangePlanClick={onChangePlanClick}
          isEditHouseLoading={isEditHouseLoading}
          onEditHouseClick={onEditHouseClick}
          isJointBillCheckboxDisabled={isJointBillCheckboxDisabled}
          isJointBillCheckboxLoading={isJointBillCheckboxLoading}
          jointBillSelectionState={jointBillSelectionState}
          onJointBillCheckboxClick={onJointBillCheckboxClick}
          isBillDownloadEnabled={isBillDownloadEnabled}
          onBillDownloadClick={onBillDownloadClick}
          isBillDownloadLoading={isBillDownloadLoading}
          projectTypeId={projectTypeId}
          isProjectBillDownloadEnabled={isProjectBillDownloadEnabled}
          onProjectBillDownloadClick={onProjectBillDownloadClick}
          isProjectBillDownloadLoading={isProjectBillDownloadLoading}
          isOpenPeriodButtonEnabled={isOpenPeriodButtonEnabled}
          onOpenPeriodButtonClick={onOpenPeriodButtonClick}
        />
      }
      headerNotification={notification}
      footerTotalCell={
        <JointBillFooterTotalCell
          amount={isArchiveMode ? totalSum : totalAmount}
          isJointBillCheckboxDisabled={isJointBillCheckboxDisabled}
          jointBillSelectionState={jointBillSelectionState}
          onJointBillCheckboxClick={onJointBillCheckboxClick}
        />
      }
      isEmpty={periodState === PeriodState.NoProviders || !bills?.length}
      emptyStateCell={
        <JointBillEmptyProvidersMessage
          preselectedJointBillServiceId={jointBillServiceId}
        />
      }
      isBillDefaultOpen={isBillDefaultOpen}
      canBePaid={canBePaid}
    >
      {billsToShow.map((bill) => (
        <ProviderBill
          key={bill.jointBillServiceAgreementId}
          jointBillServiceId={jointBillServiceId}
          periodId={periodId}
          billData={bill}
          billsCount={bills.length}
          providerBillPlanName={providerBillPlanName}
          periodState={periodState}
        />
      ))}
      {donationsToShow.map((donation) => (
        <DonationBill
          key={donation.donationServiceProviderId}
          jointBillServiceId={jointBillServiceId}
          periodId={periodId}
          donationData={donation}
        />
      ))}
      {!isSummaryMode &&
        isRequestedServiceProvidersCollapseEnabled &&
        (!!poaSpRequests?.length || !!requestedSpBillData.length) && (
          <RequestedServiceProviders
            requestedSpBillData={requestedSpBillData}
            jointBillServiceId={jointBillServiceId}
            periodId={periodId}
            poaSpRequests={poaSpRequests}
          />
        )}
      {shouldDisplayNonProfits && (
        <NonProfitProviders
          jointBillServiceId={jointBillServiceId}
          periodId={periodId}
        />
      )}
      {isSummaryMode && shouldDisplayMore && (
        <JointBillTableRowLayout
          nameCell={
            <ButtonLink
              variant="link"
              className="font-normal text-[#0044CC]"
              href="/bills"
            >
              {t("bill.interactions.openBillsPage.label")}
            </ButtonLink>
          }
        />
      )}
      {!isSummaryMode &&
        !isRequestedServiceProvidersCollapseEnabled &&
        requestedSpBillData?.map((data, index) => (
          <RequestedSpBill
            key={index}
            jointBillServiceId={jointBillServiceId}
            periodId={periodId}
            requestedSpBillData={data}
            fullWidthBorderTop={
              isNonProfitProvidersEnabled &&
              !isNullOrUndefined(nonProfitProviders) &&
              nonProfitProviders.length > 0 &&
              index === 0
            }
          />
        ))}

      {!isArchiveMode && <JointBillAdverts />}
      {!isArchiveMode && isServiceProviderCategorySuggestionEnabled && (
        <FeatureFlag flag="isProviderFilteringByCategoryEnabled">
          <ProviderCategories jointBillServiceId={jointBillServiceId} />
        </FeatureFlag>
      )}
      {!isSummaryMode && (
        <FeatureFlag flag={FEATURE_FLAG.voucherActivation}>
          <JointBillVoucher
            totalExpectedPayment={totalExpectedPayment}
            totalSum={totalSum}
            vouchers={vouchers}
            jointBillServiceId={jointBillServiceId}
            canBePaid={canBePaid}
          />
        </FeatureFlag>
      )}
    </JointBillTableLayout>
  );
};
